/**
 * VoiceRecorder — WhatsApp-standard voice recorder with gesture controls.
 *
 * Gesture states:
 * - holding: user pressing mic button, recording in progress
 *   → swipe left to cancel, swipe up to lock, release to send
 * - locked: hands-free mode, tap stop/trash/send
 * - preview: recording stopped, play/pause + send/trash
 * - cancelled: brief exit, calls onCancel
 *
 * Live waveform from AnalyserNode frequency bins.
 * Real waveform peaks in playback preview.
 *
 * Error handling:
 * - ALL mic errors show "How to Fix" instructions (not a useless retry loop)
 * - MIC_BLOCKED: browser-specific numbered steps + auto-resume via onchange
 * - MIC_NO_SECURE_CTX: explains HTTPS requirement
 * - MIC_NOT_FOUND / MIC_IN_USE: actionable guidance + Retry
 * - MIC_UNKNOWN: shows raw error detail for debugging
 */

import { useState, useCallback, useEffect, useRef, memo } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import {
  useVoiceRecorder,
  formatRecordingDuration,
  type VoiceRecorderResult,
  type MicPermissionState,
  type MicErrorCode,
} from '../hooks/useVoiceRecorder';
import { chatHaptic } from '../utils/chatHaptic';

// Browser detection for permission instructions
function detectBrowser(): 'chrome' | 'safari' | 'firefox' | 'other' {
  const ua = navigator.userAgent;
  if (/CriOS|Chrome/.test(ua) && !/Edg/.test(ua)) return 'chrome';
  if (/Safari/.test(ua) && !/Chrome/.test(ua)) return 'safari';
  if (/Firefox/.test(ua)) return 'firefox';
  return 'other';
}

type GestureState = 'idle' | 'holding' | 'locked' | 'preview' | 'cancelled';

const CANCEL_THRESHOLD = 100; // px swipe left to cancel
const LOCK_THRESHOLD = 80; // px swipe up to lock
const BAR_COUNT = 32;

// =============================================================================
// Live Waveform (recording) — driven by real frequency bins
// =============================================================================

const LiveWaveform = memo(function LiveWaveform({
  frequencyBins,
}: {
  frequencyBins: number[];
}) {
  return (
    <div className="flex h-8 flex-1 items-center justify-center gap-[2px]">
      {Array.from({ length: BAR_COUNT }, (_, i) => {
        const bin = frequencyBins[i] ?? 0;
        const heightPct = Math.max(15, (bin / 255) * 100);
        return (
          <div
            key={i}
            className="w-[3px] rounded-full bg-error-400"
            style={{
              height: `${heightPct}%`,
              transition: 'height 60ms ease-out',
            }}
          />
        );
      })}
    </div>
  );
});

// =============================================================================
// Preview Waveform — driven by real waveform peaks
// =============================================================================

const PreviewWaveform = memo(function PreviewWaveform({
  peaks,
  progress,
  isPlaying,
}: {
  peaks: number[];
  progress: number;
  isPlaying: boolean;
}) {
  const barCount = 35;
  // Resample peaks to barCount bars
  const bars = Array.from({ length: barCount }, (_, i) => {
    if (peaks.length === 0) return 0.2;
    const idx = Math.floor((i / barCount) * peaks.length);
    return Math.max(0.15, Math.min(1, peaks[idx] ?? 0.2));
  });

  return (
    <div className="flex h-8 flex-1 items-center gap-[2px]">
      {bars.map((height, i) => {
        const barProgress = i / barCount;
        const isActive = barProgress <= progress;
        return (
          <div
            key={i}
            className="w-[3px] rounded-full transition-colors duration-150"
            style={{
              height: `${height * 100}%`,
              backgroundColor: isActive ? 'var(--color-neutral-700)' : 'rgba(0, 44, 65, 0.2)',
              transform: isPlaying && isActive ? 'scaleY(1.1)' : 'scaleY(1)',
              transition: 'height 100ms ease-out, background-color 150ms, transform 200ms',
            }}
          />
        );
      })}
    </div>
  );
});

// =============================================================================
// MicErrorUI — Renders error-specific instructional content
// =============================================================================

function MicErrorUI({
  error,
  errorDetail,
  micPermission: _micPermission,
  onRetry,
  onCancel,
}: {
  error: MicErrorCode;
  errorDetail: string | null;
  micPermission: MicPermissionState;
  onRetry: () => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const browser = detectBrowser();

  // Permission-related errors — show how to fix in browser settings
  if (error === 'MIC_BLOCKED' || error === 'MIC_NO_SECURE_CTX') {
    return (
      <>
        <div className="flex items-center gap-2 text-neutral-800">
          <svg className="h-5 w-5 text-error-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="1" y1="1" x2="23" y2="23" />
            <path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6" />
            <path d="M17 16.95A7 7 0 015 12v-2m14 0v2c0 .84-.15 1.65-.42 2.4" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
          <span className="text-sm font-bold">
            {error === 'MIC_NO_SECURE_CTX'
              ? t('messaging.micNoSecureCtx', 'Microphone requires HTTPS')
              : t('messaging.micBlocked', 'Microphone blocked')}
          </span>
        </div>

        {error === 'MIC_NO_SECURE_CTX' ? (
          <p className="max-w-[300px] text-center text-xs leading-relaxed text-neutral-800/60">
            {t('messaging.micNoSecureCtxHelp', 'Voice recording requires a secure connection (HTTPS or localhost). Contact your administrator.')}
          </p>
        ) : (
          /* Browser-specific numbered instructions */
          <div className="max-w-[300px] rounded-lg bg-white/60 px-3 py-2 text-start">
            {browser === 'safari' ? (
              <ol className="list-inside list-decimal space-y-1 text-xs leading-relaxed text-neutral-800/70">
                <li>{t('messaging.micStepSafari1', 'Tap AA in the address bar')}</li>
                <li>{t('messaging.micStepSafari2', 'Tap "Website Settings"')}</li>
                <li>{t('messaging.micStepSafari3', 'Set Microphone to "Allow"')}</li>
              </ol>
            ) : browser === 'firefox' ? (
              <ol className="list-inside list-decimal space-y-1 text-xs leading-relaxed text-neutral-800/70">
                <li>{t('messaging.micStepFirefox1', 'Tap the lock icon in the address bar')}</li>
                <li>{t('messaging.micStepFirefox2', 'Tap "Connection secure" → "More information"')}</li>
                <li>{t('messaging.micStepFirefox3', 'Under Permissions, allow Microphone')}</li>
              </ol>
            ) : (
              <ol className="list-inside list-decimal space-y-1 text-xs leading-relaxed text-neutral-800/70">
                <li>{t('messaging.micStepChrome1', 'Tap the lock/tune icon in the address bar')}</li>
                <li>{t('messaging.micStepChrome2', 'Find "Microphone" and set it to "Allow"')}</li>
                <li>{t('messaging.micStepChrome3', 'Recording will resume automatically')}</li>
              </ol>
            )}
          </div>
        )}

        {error === 'MIC_BLOCKED' && (
          <p className="text-[10px] text-neutral-800/40">
            {t('messaging.micAutoResume', 'Recording will start automatically once enabled')}
          </p>
        )}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg border border-slate-400 bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 transition-all active:scale-95"
          >
            {t('messaging.micEnabledRefresh', "I've enabled it — Refresh")}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="text-xs font-bold text-neutral-800/40 underline"
          >
            {t('common.close', 'Close')}
          </button>
        </div>

        {/* Debug detail (small, dimmed) */}
        {errorDetail && (
          <p className="max-w-[300px] break-all text-center text-[9px] tabular-nums text-neutral-800/25">
            {errorDetail}
          </p>
        )}
      </>
    );
  }

  // Device / transient errors — Retry is meaningful here
  const titles: Record<string, string> = {
    MIC_NOT_FOUND: t('messaging.micNotFound', 'No microphone detected'),
    MIC_IN_USE: t('messaging.micInUse', 'Microphone is in use'),
    MIC_OVERCONSTRAINED: t('messaging.micOverconstrained', 'Microphone not compatible'),
    MIC_UNKNOWN: t('messaging.micError', 'Recording failed'),
  };

  const helpTexts: Record<string, string> = {
    MIC_NOT_FOUND: t('messaging.micNotFoundHelp', 'Connect a microphone and try again.'),
    MIC_IN_USE: t('messaging.micInUseHelp', 'Close other apps using the microphone and try again.'),
    MIC_OVERCONSTRAINED: t('messaging.micOverconstrainedHelp', 'Your microphone may not be compatible. Try a different device.'),
    MIC_UNKNOWN: t('messaging.micUnknownHelp', 'Something went wrong. Check the console for details.'),
  };

  return (
    <>
      <div className="flex items-center gap-2 text-neutral-800">
        <svg className="h-5 w-5 text-warning-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
          <path d="M19 10v2a7 7 0 01-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
        <span className="text-sm font-bold">
          {titles[error] ?? titles.MIC_UNKNOWN}
        </span>
      </div>
      <p className="max-w-[280px] text-center text-xs leading-relaxed text-neutral-800/60">
        {helpTexts[error] ?? helpTexts.MIC_UNKNOWN}
      </p>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onRetry}
          className="rounded-lg border border-slate-400 bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 transition-all active:scale-95"
        >
          {t('common.retry', 'Retry')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs font-bold text-neutral-800/40 underline"
        >
          {t('common.close', 'Close')}
        </button>
      </div>

      {/* Debug detail */}
      {errorDetail && (
        <p className="max-w-[300px] break-all text-center text-[9px] tabular-nums text-neutral-800/25">
          {errorDetail}
        </p>
      )}
    </>
  );
}

// =============================================================================
// VoiceRecorder
// =============================================================================

interface VoiceRecorderProps {
  onRecordingComplete: (result: VoiceRecorderResult) => void;
  onCancel?: () => void;
  onStartRecording?: () => void;
}

export const VoiceRecorder = memo(function VoiceRecorder({
  onRecordingComplete,
  onCancel,
  onStartRecording,
}: VoiceRecorderProps) {
  const { t } = useTranslation();
  const isRtl = document.documentElement.dir === 'rtl' || document.documentElement.lang === 'he';
  const { state, startRecording, stopRecording, cancelRecording } = useVoiceRecorder();

  const [gestureState, setGestureState] = useState<GestureState>('idle');
  const [pendingResult, setPendingResult] = useState<VoiceRecorderResult | null>(null);
  const [cancelProgress, setCancelProgress] = useState(0);
  const [lockProgress, setLockProgress] = useState(0);

  // Pointer tracking
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const cancelThresholdCrossedRef = useRef(false);
  const lockThresholdCrossedRef = useRef(false);

  // Audio playback
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const mountedRef = useRef(false);

  const isRecording = state.status === 'recording';
  const prevMicPermRef = useRef<MicPermissionState>(state.micPermission);

  // Auto-start recording when component mounts.
  // Uses a ref guard to prevent React StrictMode double-invoke from racing.
  // No haptic here — this runs outside user gesture context (Chrome blocks vibrate).
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    if (gestureState === 'idle' && !pendingResult) {
      onStartRecording?.();
      startRecording().then((ok) => {
        if (ok) setGestureState('holding');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-resume: when micPermission transitions from denied→granted
  // (user fixed permission in browser settings → onchange listener fired),
  // automatically clear the error state and attempt to start recording.
  useEffect(() => {
    const prev = prevMicPermRef.current;
    prevMicPermRef.current = state.micPermission;

    if (
      prev === 'denied' &&
      state.micPermission === 'granted' &&
      gestureState === 'idle' &&
      state.status === 'idle'
    ) {
      startRecording().then((ok) => {
        if (ok) setGestureState('holding');
      });
    }
  }, [state.micPermission, state.status, gestureState, startRecording]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Reset playback when result changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
    setPlaybackProgress(0);
  }, [pendingResult]);

  // ------ Recording lifecycle ------

  // Smart retry: only retries for transient errors (MIC_NOT_FOUND, MIC_IN_USE).
  // For MIC_BLOCKED, the MicErrorUI shows "How to Fix" instead of Retry.
  const handleRecordStart = useCallback(async () => {
    chatHaptic.voiceTap();
    onStartRecording?.();
    const ok = await startRecording();
    if (ok) {
      setGestureState('holding');
    }
  }, [startRecording, onStartRecording]);

  const handleStopAndSend = useCallback(async () => {
    chatHaptic.send();
    const result = await stopRecording();
    if (result) {
      onRecordingComplete(result);
    }
  }, [stopRecording, onRecordingComplete]);

  const handleStopToPreview = useCallback(async () => {
    chatHaptic.voiceTap();
    const result = await stopRecording();
    if (result) {
      setPendingResult(result);
      setGestureState('preview');
    }
  }, [stopRecording]);

  const handleCancelRecording = useCallback(() => {
    chatHaptic.action('delete');
    if (isRecording) cancelRecording();
    if (pendingResult) URL.revokeObjectURL(pendingResult.url);
    setPendingResult(null);
    setCancelProgress(0);
    setLockProgress(0);
    setGestureState('idle');
    onCancel?.();
  }, [isRecording, cancelRecording, pendingResult, onCancel]);

  // ------ Gesture handlers ------

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (gestureState !== 'holding' && gestureState !== 'idle') return;
      if (gestureState === 'holding') {
        pointerStartRef.current = { x: e.clientX, y: e.clientY };
        cancelThresholdCrossedRef.current = false;
        lockThresholdCrossedRef.current = false;
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      }
    },
    [gestureState]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (gestureState !== 'holding' || !pointerStartRef.current) return;

      const deltaX = e.clientX - pointerStartRef.current.x;
      const deltaY = e.clientY - pointerStartRef.current.y;

      // Cancel: swipe left in LTR, swipe right in RTL
      const cancelDelta = isRtl ? deltaX : -deltaX;
      const newCancelProgress = Math.min(1, Math.max(0, cancelDelta / CANCEL_THRESHOLD));
      setCancelProgress(newCancelProgress);

      // Lock: swipe up
      const lockDelta = -deltaY;
      const newLockProgress = Math.min(1, Math.max(0, lockDelta / LOCK_THRESHOLD));
      setLockProgress(newLockProgress);

      if (newCancelProgress >= 1 && !cancelThresholdCrossedRef.current) {
        cancelThresholdCrossedRef.current = true;
        chatHaptic.swipeThreshold();
        handleCancelRecording();
        return;
      }

      if (newLockProgress >= 1 && !lockThresholdCrossedRef.current) {
        lockThresholdCrossedRef.current = true;
        chatHaptic.swipeProgress();
        setGestureState('locked');
        setCancelProgress(0);
        setLockProgress(0);
        pointerStartRef.current = null;
      }
    },
    [gestureState, isRtl, handleCancelRecording]
  );

  const handlePointerUp = useCallback(() => {
    if (gestureState === 'holding') {
      pointerStartRef.current = null;
      setCancelProgress(0);
      setLockProgress(0);
      handleStopAndSend();
      setGestureState('idle');
    }
  }, [gestureState, handleStopAndSend]);

  // ------ Locked mode actions ------

  const handleLockedSend = useCallback(async () => {
    chatHaptic.send();
    const result = await stopRecording();
    if (result) onRecordingComplete(result);
    setGestureState('idle');
  }, [stopRecording, onRecordingComplete]);

  const handleLockedStop = useCallback(async () => {
    await handleStopToPreview();
  }, [handleStopToPreview]);

  // ------ Preview mode actions ------

  const handlePreviewSend = useCallback(() => {
    if (!pendingResult) return;
    chatHaptic.send();
    onRecordingComplete(pendingResult);
    setPendingResult(null);
    setGestureState('idle');
  }, [pendingResult, onRecordingComplete]);

  const handlePreviewTrash = useCallback(() => {
    chatHaptic.action('delete');
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (pendingResult) URL.revokeObjectURL(pendingResult.url);
    setPendingResult(null);
    setIsPlaying(false);
    setPlaybackProgress(0);
    setGestureState('idle');
    onCancel?.();
  }, [pendingResult, onCancel]);

  const handlePlayPause = useCallback(() => {
    if (!pendingResult) return;
    chatHaptic.voiceTap();

    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    if (!audioRef.current) {
      audioRef.current = new Audio(pendingResult.url);
      audioRef.current.ontimeupdate = () => {
        if (audioRef.current) {
          const p = audioRef.current.currentTime / audioRef.current.duration;
          setPlaybackProgress(isNaN(p) ? 0 : p);
        }
      };
      audioRef.current.onended = () => {
        setIsPlaying(false);
        setPlaybackProgress(0);
      };
    }

    audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
  }, [pendingResult, isPlaying]);

  // ------ Render ------

  // Holding state: recording with gesture tracking
  if (gestureState === 'holding' && isRecording) {
    return (
      <div
        className="flex-shrink-0 border-t border-white/20 bg-white/40 px-3 py-2 pb-safe glass-surface"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        style={{ touchAction: 'none' }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.15 }}
          className="flex w-full items-center gap-2"
        >
          {/* Slide to cancel hint */}
          <div className="flex items-center gap-1 text-xs text-error-400" style={{ opacity: 0.5 + cancelProgress * 0.5 }}>
            <motion.svg
              className="h-4 w-4 rtl:rotate-180"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              animate={{ x: isRtl ? [0, 5, 0] : [0, -5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              <polyline points="15 18 9 12 15 6" />
            </motion.svg>
            <span>{t('messaging.slideToCancel', 'Slide to cancel')}</span>
          </div>

          {/* Center: waveform + duration */}
          <div className="flex flex-1 items-center gap-2">
            <motion.div
              className="h-3 w-3 flex-shrink-0 rounded-full bg-error-500"
              animate={{ scale: [1, 1.3, 1], opacity: [1, 0.6, 1] }}
              transition={{ duration: 0.8, repeat: Infinity, ease: 'easeInOut' }}
            />
            <span className="min-w-[36px] text-sm font-bold tabular-nums text-neutral-800">
              {formatRecordingDuration(state.duration)}
            </span>
            <LiveWaveform frequencyBins={state.frequencyBins} />
          </div>

          {/* Lock hint */}
          <div
            className="flex flex-col items-center"
            style={{ opacity: 0.4 + lockProgress * 0.6 }}
          >
            <motion.svg
              className="h-5 w-5 text-neutral-800/60"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </motion.svg>
          </div>
        </motion.div>
      </div>
    );
  }

  // Locked state: hands-free recording
  if (gestureState === 'locked' && isRecording) {
    return (
      <div className="flex-shrink-0 border-t border-white/20 bg-white/40 px-3 py-2 pb-safe glass-surface">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.15 }}
          className="flex w-full items-center gap-2"
        >
          {/* Trash / Cancel */}
          <button
            type="button"
            onClick={handleCancelRecording}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-error-400 bg-error-50 text-error-500 transition-all active:scale-95"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            </svg>
          </button>

          {/* Waveform + duration */}
          <div className="flex min-h-[40px] flex-1 items-center gap-2 rounded-xl border border-white/20 bg-white/80 px-3 py-2">
            <motion.div
              className="h-3 w-3 flex-shrink-0 rounded-full bg-error-500"
              animate={{ scale: [1, 1.3, 1], opacity: [1, 0.6, 1] }}
              transition={{ duration: 0.8, repeat: Infinity, ease: 'easeInOut' }}
            />
            <span className="text-sm font-bold tabular-nums text-neutral-800">
              {formatRecordingDuration(state.duration)}
            </span>
            <LiveWaveform frequencyBins={state.frequencyBins} />
            {/* Stop button */}
            <button
              type="button"
              onClick={handleLockedStop}
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100 text-neutral-800"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                <rect x="4" y="4" width="16" height="16" rx="2" />
              </svg>
            </button>
          </div>

          {/* Send button */}
          <button
            type="button"
            onClick={handleLockedSend}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-white/20 bg-slate-700 text-white shadow-sm transition-all active:translate-y-[1px] active:shadow-none"
          >
            <svg className="h-5 w-5 -rotate-45 rtl:rotate-45" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </motion.div>
      </div>
    );
  }

  // Preview state: recording done, play/pause + send/trash
  if (gestureState === 'preview' && pendingResult) {
    return (
      <div className="flex-shrink-0 border-t border-white/20 bg-white/40 px-3 py-2 pb-safe glass-surface">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.15 }}
          className="flex w-full items-center gap-2"
        >
          {/* Trash */}
          <button
            type="button"
            onClick={handlePreviewTrash}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-error-400 bg-error-50 text-error-500 transition-all active:scale-95"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            </svg>
          </button>

          {/* Preview playback */}
          <div className="flex min-h-[40px] flex-1 items-center gap-2 rounded-xl border border-white/20 bg-white/80 px-3 py-2">
            <button
              type="button"
              onClick={handlePlayPause}
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600"
            >
              {isPlaying ? (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </svg>
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              )}
            </button>

            <PreviewWaveform
              peaks={pendingResult.waveformPeaks}
              progress={playbackProgress}
              isPlaying={isPlaying}
            />

            <span className="min-w-[36px] text-end text-xs font-bold tabular-nums text-neutral-800/60">
              {formatRecordingDuration(pendingResult.duration)}
            </span>
          </div>

          {/* Send */}
          <button
            type="button"
            onClick={handlePreviewSend}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-white/20 bg-slate-700 text-white shadow-sm transition-all active:translate-y-[1px] active:shadow-none"
          >
            <svg className="h-5 w-5 -rotate-45 rtl:rotate-45" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </motion.div>
      </div>
    );
  }

  // Idle / requesting / error fallback
  return (
    <div className="flex-shrink-0 border-t border-white/20 bg-white/40 px-3 py-2 pb-safe glass-surface">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.15 }}
        className="flex w-full flex-col items-center gap-2 py-2"
      >
        {state.status === 'requesting' ? (
          <div className="flex items-center gap-2 text-sm text-neutral-800/60">
            <div className="h-4 w-4 animate-spin rounded-full border border-white/20 border-t-slate-800" />
            <span>{t('messaging.requestingMicrophone', 'Requesting microphone...')}</span>
          </div>
        ) : state.error ? (
          <MicErrorUI
            error={state.error}
            errorDetail={state.errorDetail}
            micPermission={state.micPermission}
            onRetry={handleRecordStart}
            onCancel={onCancel}
          />
        ) : (
          <button
            type="button"
            onClick={onCancel}
            className="text-xs font-bold text-neutral-800/60 underline"
          >
            {t('common.cancel', 'Cancel')}
          </button>
        )}
      </motion.div>
    </div>
  );
});

export default VoiceRecorder;
